import {
    Avatar,
    Divider,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    MenuItem,
    Stack,
} from '@mui/material';
import { PageListItem } from '../../api/types/cms';
import { useEffect, useMemo, useState } from 'react';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    ColumnWidthOutlined,
    CopyOutlined,
    DeleteOutlined,
    EditOutlined,
    GlobalOutlined,
    HomeOutlined,
    PlusCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { ColumEntry } from '../../pages/cms/Pages';
import ContextMenu from '../qlive/ContextMenu';
import { useTranslation } from '../../contexts/TranslationContext';
import { useTheme } from '@mui/material/styles';

export type PageContextCallbacks = {
    edit: (page: PageListItem) => void;
    setOnline: (
        page: PageListItem,
        columnIndex: number,
        parent: PageListItem | null,
    ) => void;
    setOffline: (
        page: PageListItem,
        columnIndex: number,
        parent: PageListItem | null,
    ) => void;
    setHome: (
        page: PageListItem,
        columnIndex: number,
        parent: PageListItem | null,
    ) => void;
    movePage: (page: PageListItem) => void;
    deletePage: (
        page: PageListItem,
        columnIndex: number,
        parent: PageListItem | null,
    ) => void;
    onPageSelect?: (page: PageListItem) => void;
    copyPage: (
        page: PageListItem,
        columnIndex: number,
        parent: PageListItem | null,
    ) => void;
};

type PagesColumnProps = {
    column: ColumEntry;
    columnIndex: number;
    loadChildren: (colIndex: number, parentPage: PageListItem) => void;
    border?: boolean;
    allowActions?: boolean;
    callbacks?: PageContextCallbacks;
};

export default function PagesColumn({
    column,
    columnIndex,
    loadChildren,
    border = true,
    allowActions = true,
    callbacks,
}: PagesColumnProps) {
    const theme = useTheme();
    const navigate = useNavigate();
    const { translate } = useTranslation();

    const [selectedPage, setSelectedPage] = useState<PageListItem | null>(null);
    const [showBtn, setShowBtn] = useState(false);

    useEffect(() => {
        if (column && column.resetSelection) {
            setSelectedPage(null);
        }
    }, [column]);

    const onMouseEnterCol = () => {
        setShowBtn(true);
    };

    const onMouseLeaveCol = () => {
        setShowBtn(false);
    };

    const addNewPage = () => {
        let path = `/cms/pages/index/new/${column.locale}`;
        if (column.parent) {
            path = `/cms/pages/${column.parent.id}/new/${column.locale}`;
        }
        navigate(path);
    };

    const renderPages = () => {
        if (column.pages.length === 0) {
            return (
                <Stack
                    direction="column"
                    sx={{ height: '80%' }}
                    alignItems="center"
                    justifyContent="center">
                    <ListItem>
                        <ListItemText>
                            {translate('pages.cms.pages.no_subpage')}
                        </ListItemText>
                    </ListItem>
                </Stack>
            );
        }

        return column.pages.map((page, index) => {
            const selectPage = () => {
                loadChildren(columnIndex + 1, page);
                if (callbacks?.onPageSelect) {
                    callbacks.onPageSelect(page);
                }
                setSelectedPage(page);
            };

            const getContextMenuItems = (handleMenuClose: () => void) => {
                const onEdit = () => {
                    callbacks?.edit(page);
                    handleMenuClose();
                };

                const onCopy = () => {
                    callbacks?.copyPage(page, columnIndex, column.parent);
                    handleMenuClose();
                };

                const onMove = () => {
                    if (selectedPage) {
                        callbacks?.movePage(page);
                    }
                    handleMenuClose();
                };

                const onHome = () => {
                    callbacks?.setHome(page, columnIndex, column.parent);
                    handleMenuClose();
                };

                const onOffline = () => {
                    callbacks?.setOffline(page, columnIndex, column.parent);
                    handleMenuClose();
                };

                const onOnline = () => {
                    callbacks?.setOnline(page, columnIndex, column.parent);
                    handleMenuClose();
                };

                const onDelete = () => {
                    callbacks?.deletePage(page, columnIndex, column.parent);
                    handleMenuClose();
                };

                let items = [
                    <MenuItem key="edit" onClick={onEdit} sx={{ gap: 1 }}>
                        <EditOutlined style={{ fontSize: 18 }} />
                        {translate('pages.cms.pages.context.edit')}
                    </MenuItem>,
                    <MenuItem key="copy" onClick={onCopy} sx={{ gap: 1 }}>
                        <CopyOutlined style={{ fontSize: 18 }} />
                        {translate('pages.cms.pages.context.copy')}
                    </MenuItem>,
                ];

                if (!page.home_page) {
                    const subItems = [
                        <MenuItem key="move" onClick={onMove} sx={{ gap: 1 }}>
                            <ColumnWidthOutlined style={{ fontSize: 18 }} />
                            {translate('pages.cms.pages.context.move')}
                        </MenuItem>,
                    ];
                    if (columnIndex === 0) {
                        subItems.push(
                            <MenuItem
                                key="set-home"
                                onClick={onHome}
                                sx={{ gap: 1 }}>
                                <HomeOutlined style={{ fontSize: 18 }} />
                                {translate('pages.cms.pages.context.home')}
                            </MenuItem>,
                        );
                    }
                    items = [...items, ...subItems];
                }
                if (page.online) {
                    items = [
                        ...items,
                        ...[
                            <Divider key="state-divider" />,
                            <MenuItem
                                key="set-offline"
                                onClick={onOffline}
                                sx={{ gap: 1 }}>
                                <CloseCircleOutlined
                                    style={{
                                        fontSize: 18,
                                        color: theme.palette.warning.main,
                                    }}
                                />
                                {translate('pages.cms.pages.context.offline')}
                            </MenuItem>,
                        ],
                    ];
                } else {
                    items = [
                        ...items,
                        ...[
                            <Divider key="state-divider" />,
                            <MenuItem
                                key="set-online"
                                onClick={onOnline}
                                sx={{ gap: 1 }}>
                                <CheckCircleOutlined
                                    style={{
                                        fontSize: 18,
                                        color: theme.palette.success.main,
                                    }}
                                />
                                {translate('pages.cms.pages.context.online')}
                            </MenuItem>,
                        ],
                    ];
                }

                items = [
                    ...items,
                    ...[
                        <MenuItem
                            disabled={page.online}
                            key="delete"
                            onClick={onDelete}
                            sx={{ gap: 1 }}>
                            <DeleteOutlined
                                style={{
                                    fontSize: 18,
                                    color: theme.palette.error.main,
                                }}
                            />
                            {translate('pages.cms.pages.context.delete')}
                        </MenuItem>,
                    ],
                ];

                return items;
            };

            return (
                <ListItemButton
                    key={page.id}
                    onClick={selectPage}
                    onDoubleClick={
                        allowActions ? () => callbacks?.edit(page) : undefined
                    }
                    draggable
                    selected={
                        selectedPage ? selectedPage?.id === page.id : undefined
                    }
                    data-page={page}>
                    <ContextMenu
                        disabled={!allowActions}
                        menuItems={getContextMenuItems}
                        style={{ display: 'flex', alignItems: 'center' }}>
                        <>
                            <ListItemText
                                primary={
                                    <>
                                        {page?.home_page && (
                                            <HomeOutlined
                                                style={{ paddingRight: 6 }}
                                            />
                                        )}
                                        {page?.title}
                                    </>
                                }
                                secondary={page.slug}
                                primaryTypographyProps={{
                                    fontWeight: 'bold',
                                }}
                            />
                            <GlobalOutlined
                                title={
                                    page.online
                                        ? translate(
                                              'pages.cms.pages.status.online',
                                          )
                                        : translate(
                                              'pages.cms.pages.status.offline',
                                          )
                                }
                                style={{
                                    color: page.online
                                        ? theme.palette.success.main
                                        : theme.palette.warning.main,
                                    fontSize: 16,
                                }}
                            />
                        </>
                    </ContextMenu>
                </ListItemButton>
            );
        });
    };

    return useMemo(() => {
        if (!column) {
            return null;
        }
        return (
            <Grid
                item
                xs={12}
                onMouseEnter={onMouseEnterCol}
                onMouseLeave={onMouseLeaveCol}
                md={3}
                sx={{
                    borderRight: border ? 0.25 : 0,
                    borderColor: theme => theme.palette.divider,
                    height: '100%',
                    position: 'relative',
                }}>
                <List sx={{ height: '100%' }}>
                    {allowActions && (
                        <Stack
                            spacing={2}
                            direction="row"
                            alignItems="center"
                            justifyContent="center"
                            visibility={showBtn ? 'visible' : 'hidden'}
                            sx={{ px: 2, pb: 1 }}>
                            <IconButton
                                sx={{ flex: 1, zIndex: 4, borderRadius: 25 }}
                                color="success"
                                onClick={addNewPage}>
                                <PlusCircleOutlined />
                            </IconButton>
                        </Stack>
                    )}
                    {renderPages()}
                </List>
            </Grid>
        );
    }, [column, selectedPage, showBtn]);
}
